/**
 * Hook personnalisé pour la gestion des permissions
 */

import { useState, useEffect, useCallback } from 'react';
import permissionManagementService from '@/services/permissionManagementService';
import {
  Permission,
  Role,
  PermissionFormData,
  RoleFormData,
  AssignPermissionToRoleData,
  RemovePermissionFromRoleData,
  PermissionCategory,
  PermissionManagementStats,
  UsePermissionsReturn,
  UserRole
} from '@/types/permissionManagement';

export const usePermissionManagement = (): UsePermissionsReturn => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<PermissionManagementStats | null>(null);

  // ============================================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================================

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer toutes les permissions et rôles avec pagination complète
      const [allPermissions, allRoles, statsResponse] = await Promise.all([
        getAllPermissions(),
        getAllRoles(),
        permissionManagementService.getStats()
      ]);

      setPermissions(allPermissions);
      setRoles(allRoles);
      setStats(statsResponse);
    } catch (err: any) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour récupérer toutes les permissions avec pagination
  const getAllPermissions = async (): Promise<Permission[]> => {
    const allPermissions: Permission[] = [];
    let page = 1;
    let hasNext = true;

    while (hasNext) {
      const response = await permissionManagementService.getPermissions({ 
        page, 
        page_size: 1000 
      });
      
      allPermissions.push(...response.results);
      hasNext = response.next !== null;
      page++;
    }

    return allPermissions;
  };

  // Fonction pour récupérer tous les rôles avec pagination
  const getAllRoles = async (): Promise<Role[]> => {
    const allRoles: Role[] = [];
    let page = 1;
    let hasNext = true;

    while (hasNext) {
      const response = await permissionManagementService.getRoles({ 
        page, 
        page_size: 1000 
      });
      
      allRoles.push(...response.results);
      hasNext = response.next !== null;
      page++;
    }

    return allRoles;
  };

  // ============================================================================
  // GESTION DES PERMISSIONS
  // ============================================================================

  const createPermission = useCallback(async (data: PermissionFormData): Promise<Permission> => {
    try {
      const newPermission = await permissionManagementService.createPermission(data);
      setPermissions(prev => [...prev, newPermission]);
      await refreshData(); // Rafraîchir les stats
      return newPermission;
    } catch (err: any) {
      console.error('Erreur lors de la création de la permission:', err);
      throw err;
    }
  }, [refreshData]);

  const updatePermission = useCallback(async (id: number, data: PermissionFormData): Promise<Permission> => {
    try {
      const updatedPermission = await permissionManagementService.updatePermission(id, data);
      setPermissions(prev => prev.map(p => p.id === id ? updatedPermission : p));
      return updatedPermission;
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour de la permission:', err);
      throw err;
    }
  }, []);

  const deletePermission = useCallback(async (id: number): Promise<void> => {
    try {
      await permissionManagementService.deletePermission(id);
      setPermissions(prev => prev.filter(p => p.id !== id));
      await refreshData(); // Rafraîchir les stats
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la permission:', err);
      throw err;
    }
  }, [refreshData]);

  // ============================================================================
  // GESTION DES RÔLES
  // ============================================================================

  const createRole = useCallback(async (data: RoleFormData): Promise<Role> => {
    try {
      const newRole = await permissionManagementService.createRole(data);
      setRoles(prev => [...prev, newRole]);
      await refreshData(); // Rafraîchir les stats
      return newRole;
    } catch (err: any) {
      console.error('Erreur lors de la création du rôle:', err);
      throw err;
    }
  }, [refreshData]);

  const updateRole = useCallback(async (id: number, data: RoleFormData): Promise<Role> => {
    try {
      const updatedRole = await permissionManagementService.updateRole(id, data);
      setRoles(prev => prev.map(r => r.id === id ? updatedRole : r));
      return updatedRole;
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour du rôle:', err);
      throw err;
    }
  }, []);

  const deleteRole = useCallback(async (id: number): Promise<void> => {
    try {
      await permissionManagementService.deleteRole(id);
      setRoles(prev => prev.filter(r => r.id !== id));
      await refreshData(); // Rafraîchir les stats
    } catch (err: any) {
      console.error('Erreur lors de la suppression du rôle:', err);
      throw err;
    }
  }, [refreshData]);

  // ============================================================================
  // GESTION DES ASSOCIATIONS RÔLE-PERMISSION
  // ============================================================================

  const assignPermissionToRole = useCallback(async (data: AssignPermissionToRoleData): Promise<void> => {
    try {
      await permissionManagementService.assignPermissionToRole(data);
      await refreshData(); // Rafraîchir les données pour mettre à jour les compteurs
    } catch (err: any) {
      console.error('Erreur lors de l\'association permission-rôle:', err);
      throw err;
    }
  }, [refreshData]);

  const removePermissionFromRole = useCallback(async (data: RemovePermissionFromRoleData): Promise<void> => {
    try {
      await permissionManagementService.removePermissionFromRole(data);
      await refreshData(); // Rafraîchir les données pour mettre à jour les compteurs
    } catch (err: any) {
      console.error('Erreur lors de la suppression de l\'association permission-rôle:', err);
      throw err;
    }
  }, [refreshData]);

  const getRolePermissions = useCallback(async (roleId: number): Promise<Permission[]> => {
    try {
      return await permissionManagementService.getRolePermissionsById(roleId);
    } catch (err: any) {
      console.error('Erreur lors de la récupération des permissions du rôle:', err);
      throw err;
    }
  }, []);

  const getPermissionsByCategory = useCallback(async (): Promise<PermissionCategory[]> => {
    try {
      return await permissionManagementService.getPermissionsByCategory();
    } catch (err: any) {
      console.error('Erreur lors de la récupération des permissions par catégorie:', err);
      throw err;
    }
  }, []);

  const getRoleUsers = useCallback(async (roleId: number): Promise<UserRole[]> => {
    try {
      return await permissionManagementService.getRoleUsers(roleId);
    } catch (err: any) {
      console.error('Erreur lors de la récupération des utilisateurs du rôle:', err);
      throw err;
    }
  }, []);

  // ============================================================================
  // INITIALISATION
  // ============================================================================

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    permissions,
    roles,
    loading,
    error,
    stats,
    refreshData,
    createPermission,
    updatePermission,
    deletePermission,
    createRole,
    updateRole,
    deleteRole,
    assignPermissionToRole,
    removePermissionFromRole,
    getRolePermissions,
    getPermissionsByCategory,
    getRoleUsers
  };
};
